import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  "Which MAX tread is right for my truck?",
  "What sizes does MAX LM come in?",
  "Do you have 1200x24 treads?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Namaste! I'm the MAXIMUS assistant. Ask me about our MAX precured tread range, bonding gum or vulcanising solution — or tell me your vehicle and usage and I'll point you to the right tread.",
    },
  ]);
  const [session] = useState(() => {
    let id = sessionStorage.getItem("mx-chat-session");
    if (!id) {
      id = `web-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      sessionStorage.setItem("mx-chat-session", id);
    }
    return id;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + data.delta };
                return c;
              });
            }
            if (data.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content || data.error };
                return c;
              });
            }
          } catch (e) { /* skip malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: c[c.length - 1].content || "I couldn't connect just now. Please call +91 9612899617 or email support@maximustreads.in.",
        };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "flex-1 bg-white/5 border border-white/15 focus:border-amber px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[390px] h-[540px] max-h-[calc(100vh-8rem)] flex flex-col bg-deep border border-amber/40 shadow-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-ink px-4 py-3">
              <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
              <div className="flex-1 leading-tight">
                <p className="font-display font-bold uppercase tracking-wide text-white text-sm">MAXIMUS Assistant</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber">Powered by Claude</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1" aria-label="Close chat" data-testid="chat-close-button">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "ml-auto bg-signal text-white"
                      : "bg-panel border border-white/10 text-slate-200"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="pt-2 space-y-2">
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      className="block w-full text-left border border-amber/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-amber hover:bg-amber hover:text-ink transition-colors"
                      data-testid="chat-chip"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-white/10 bg-ink p-3 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about treads, sizes, fitment..."
                className={inputCls}
                data-testid="chat-input"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="bg-signal hover:bg-signalhover disabled:opacity-50 px-4 text-white transition-colors"
                aria-label="Send message"
                data-testid="chat-send-button"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-6 right-4 sm:right-6 z-50 grid place-items-center h-14 w-14 rounded-full bg-signal text-white shadow-xl shadow-signal/30 hover:bg-signalhover transition-colors"
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        data-testid="chat-toggle-button"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>
    </>
  );
};

export default ChatWidget;
